import { useGoogleMapContext } from 'modules/shared/features/google-map/context/GoogleMapContext';
import { FC, useEffect, useRef } from 'react';

/* -------------------------------------------------------------------------------------------------
 * Component: GoogleMapPolygon
 * -----------------------------------------------------------------------------------------------*/

interface GoogleMapPolygonProps {
    center: google.maps.LatLngLiteral;
    angle: number; // Direction angle in degrees (0 = North, 90 = East, 180 = South, 270 = West)
    range: number; // Range in meters
    fieldOfView: number; // Camera field of view in degrees (e.g., 60, 90, 120)
    fillColor?: string;
    strokeColor?: string;
    fillOpacity?: number;
    strokeOpacity?: number;
    strokeWeight?: number;
    onClick?: () => void;
}

const GoogleMapPolygon: FC<GoogleMapPolygonProps> = (props) => {
    const {
        center,
        angle,
        range,
        fieldOfView,
        fillColor = '#4285F4',
        strokeColor = '#1967D2',
        fillOpacity = 0.35,
        strokeOpacity = 0.8,
        strokeWeight = 2,
        onClick,
    } = props;

    const { map } = useGoogleMapContext();
    const polygonRef = useRef<google.maps.Polygon | null>(null);

    useEffect(() => {
        if (!map) return;

        // Function to calculate points for the camera view cone
        const createCameraViewPath = (
            center: google.maps.LatLngLiteral,
            angle: number,
            range: number,
            fov: number
        ): google.maps.LatLngLiteral[] => {
            const points: google.maps.LatLngLiteral[] = [];

            // Start with the center point (camera position)
            points.push(center);

            // Convert range from meters to degrees (approximate)
            // 1 degree latitude ≈ 111,320 meters
            const rangeInDegrees = range / 111320;

            // Calculate the start and end angles of the cone
            const startAngle = angle - fov / 2;
            const endAngle = angle + fov / 2;

            // Create arc points for the cone
            const segments = 30; // Number of segments for smooth arc
            for (let i = 0; i <= segments; i++) {
                const currentAngle = startAngle + (endAngle - startAngle) * (i / segments);
                const radians = (currentAngle * Math.PI) / 180;

                // Calculate point at the edge of the range
                const lat = center.lat + rangeInDegrees * Math.cos(radians);
                const lng = center.lng + (rangeInDegrees * Math.sin(radians)) / Math.cos((center.lat * Math.PI) / 180);

                points.push({ lat, lng });
            }

            // Close the polygon back to center
            points.push(center);

            return points;
        };

        const path = createCameraViewPath(center, angle, range, fieldOfView);

        // Create or update polygon
        if (!polygonRef.current) {
            polygonRef.current = new google.maps.Polygon({
                paths: path,
                strokeColor: strokeColor,
                strokeOpacity: strokeOpacity,
                strokeWeight: strokeWeight,
                fillColor: fillColor,
                fillOpacity: fillOpacity,
                map: map,
                clickable: !!onClick,
            });

            if (onClick) {
                polygonRef.current.addListener('click', onClick);
            }
        } else {
            // Update existing polygon
            polygonRef.current.setPaths(path);
            polygonRef.current.setOptions({
                strokeColor: strokeColor,
                strokeOpacity: strokeOpacity,
                strokeWeight: strokeWeight,
                fillColor: fillColor,
                fillOpacity: fillOpacity,
            });
        }

        return () => {
            if (polygonRef.current) {
                google.maps.event.clearInstanceListeners(polygonRef.current);
                polygonRef.current.setMap(null);
                polygonRef.current = null;
            }
        };
    }, [
        map,
        center,
        angle,
        range,
        fieldOfView,
        fillColor,
        strokeColor,
        fillOpacity,
        strokeOpacity,
        strokeWeight,
        onClick,
    ]);

    return null;
};

export default GoogleMapPolygon;
